import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import env from "env";

export interface LoginRequest {
  username?: string;
  password?: string;
  client_id?: string;
  code?: string;
  code_verifier?: string;
  redirect_uri?: string;
  grant_type: string; // Required field
  scope?: string;
}

export interface AuthResponse {
  access_token: string;
  expires_in: number;
  id_token: string;
  refresh_token: string;
  scope: string;
  token_type: string;
}

// Helper function to create form-encoded body
export const createFormBody = (params: Record<string, string | undefined | null>): string => {
  return Object.entries(params)
    .filter(([_, value]) => value !== undefined && value !== null && value !== "")
    .map(([key, value]) => 
      encodeURIComponent(key) + "=" + encodeURIComponent(value || "")
    )
    .join("&");
};

// Helper function to create Basic auth header safely
export const createBasicAuthHeader = (clientId: string, clientSecret: string): string => {
  try {
    if (!clientId || !clientSecret) {
      throw new Error("Client credentials missing");
    }
    return `Basic ${btoa(`${clientId}:${clientSecret}`)}`;
  } catch (error) {
    console.error("Failed to create Basic auth header:", error);
    throw error;
  }
};

// Export query functions for testing
export const createLoginQuery = (credentials: LoginRequest) => {
  const isAuthCodeFlow = credentials.grant_type === "authorization_code";
  
  // Prepare form body parameters
  const params: Record<string, string | undefined> = {
    username: credentials.username,
    password: credentials.password,
    grant_type: credentials.grant_type,
    scope: credentials.scope,
    code: credentials.code,
    code_verifier: credentials.code_verifier,
    redirect_uri: credentials.redirect_uri,
    // Only include client_id in body for authorization_code flow (no Authorization header)
    ...(isAuthCodeFlow && { client_id: credentials.client_id }),
  };

  return {
    url: "oauth2/token",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      // Use Basic auth for password grant, no auth header for auth code flow
      ...(isAuthCodeFlow 
        ? {} 
        : { Authorization: createBasicAuthHeader(env.api.clientId, env.api.clientSecret) }
      ),
    },
    method: "POST",
    body: createFormBody(params),
  };
};

export const createLogoutQuery = (refreshToken: string) => {
  // Use auth code client credentials for consistency with token refresh
  const clientId = env.app.authCodeClientId;
  const clientSecret = env.app.authCodeClientSecret;
  
  return {
    url: "oauth2/revoke",
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      // Use Basic auth for consistency with other endpoints
      Authorization: createBasicAuthHeader(clientId, clientSecret),
    },
    body: createFormBody({
      token: refreshToken,
      token_type_hint: "refresh_token", // More accurate hint for refresh tokens
    }),
  };
};

export const authApi = createApi({
  baseQuery: fetchBaseQuery({
    baseUrl: env.api.baseUrl.tokenBaseUrl,
  }),
  endpoints: (builder) => ({
    login: builder.mutation<AuthResponse, LoginRequest>({
      query: createLoginQuery,
    }),
    logout: builder.mutation<void, string>({
      query: createLogoutQuery,
    }),
  }),
});

export const { useLoginMutation, useLogoutMutation } = authApi;
